/**
 * @file CompanyEnterpriseSection.tsx
 * @brief Super Admin — provision company Enterprise seats + shared minute pool.
 *
 * @details
 * Layout (30-07-2026): Status → Plan/Money → People. Opt-in after sales close.
 * Custom Interpretation/Realtime/MI/Notes rates stay on the Billing section
 * above. Activating charges advance = included_minutes × max(MI, Notes) on the
 * company card unless Offline PO is checked. Primary company admin is
 * auto-seated on activate (ADR-020).
 *
 * @author DES
 * @date 26-07-2026
 */

import { SkeletonFields } from "@/components/ui/Skeleton";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  Briefcase,
  ChevronDown,
  CircleStop,
  ClipboardList,
  LoaderCircle,
  Pause,
  Play,
  UsersRound,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { DateTimePicker } from "@/components/ui/DateTimePicker";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { FormFieldGroup } from "@/components/ui/FormDialog";
import { FormSheetFooterCell, formSheetActionClass } from "@/components/ui/FormSheet";
import { FormSheetTabs, formSheetTabPanel, type FormSheetTab } from "@/components/ui/FormSheetTabs";
import { Input } from "@/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { ApiClientError } from "@/shared/api/client";
import { cn } from "@/shared/theme/cn";
import { SummaryItem } from "@/features/sessions/components/SessionDetailDialog";
import { toast } from "@/shared/hooks/useToast";
import { useUsersList } from "@/features/users/api";
import {
  useAssignCompanyEnterpriseSeat,
  useCompanyEnterprise,
  useRevokeCompanyEnterpriseSeat,
  useSetCompanyEnterpriseStatus,
  useUpsertCompanyEnterprise,
} from "../api";
import type { SystemSettingsResponse } from "@/features/billing/schemas";

const DEFAULT_SEAT_LIMIT = "10";
const DEFAULT_INCLUDED_MINUTES = "3000";

/** Mirrors BACKEND getCurrencyForCountry (company billing currency). */
function currencyForCountry(countryCode?: string | null): string {
  const code = (countryCode || "").trim().toUpperCase();
  if (code === "GB" || code === "UK" || code === "GBR") return "GBP";
  const eurCountries = new Set([
    "AT",
    "BE",
    "CY",
    "EE",
    "FI",
    "FR",
    "DE",
    "GR",
    "IE",
    "IT",
    "LV",
    "LT",
    "LU",
    "MT",
    "NL",
    "PT",
    "SK",
    "SI",
    "ES",
    "BG",
    "HR",
    "CZ",
    "DK",
    "HU",
    "PL",
    "RO",
    "SE",
    "IS",
    "LI",
    "NO",
    "CH",
    "AD",
    "MC",
    "SM",
    "VA",
    "AL",
    "BA",
    "ME",
    "MK",
    "RS",
    "XK",
    "MD",
    "UA",
  ]);
  if (eurCountries.has(code)) return "EUR";
  return "USD";
}

function settingMinor(settings: SystemSettingsResponse | undefined, key: string): number {
  const raw = settings?.settings?.[key];
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function defaultPeriodRange(): { start: string; end: string } {
  const start = new Date();
  const end = new Date(start);
  end.setMonth(end.getMonth() + 1);
  return { start: start.toISOString(), end: end.toISOString() };
}

function applyPeriodPreset(months: number): { start: string; end: string } {
  const start = new Date();
  const end = new Date(start);
  end.setMonth(end.getMonth() + months);
  return { start: start.toISOString(), end: end.toISOString() };
}

function formatMinor(amountMinor: number, currency: string): string {
  const code = (currency || "GBP").toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
    }).format(amountMinor / 100);
  } catch {
    return `${(amountMinor / 100).toFixed(2)} ${code}`;
  }
}

/** Major-unit form string → minor units; blank → null (use server/list). */
function majorToMinorOrNull(input?: string | null): number | null {
  if (input == null || String(input).trim() === "") return null;
  const n = Number(input);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

/** "26 Jul 2026": fits the third column; the times are in the pickers below. */
function shortDate(iso: string | Date): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Where a rate came from, in words. */
const RATE_SOURCE: Record<string, string> = {
  form: "edited above, unsaved",
  custom: "company rate",
  list: "platform rate",
};

/** "£600.00" with the currency symbol in brand red, as on Session details. */
function MoneyAmount({ text }: { text: string }) {
  const m = /^([^\d-]*)(.*)$/.exec(text);
  return (
    <>
      <span className="text-brand">{m?.[1]}</span>
      <span className="text-foreground">{m?.[2]}</span>
    </>
  );
}

function displayName(seat: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string;
}): string {
  const name = [seat.firstName, seat.lastName].filter(Boolean).join(" ").trim();
  return name || seat.email || "User";
}

export type EnterpriseRateOverrides = {
  miUsd?: string;
  miEur?: string;
  miGbp?: string;
  notesUsd?: string;
  notesEur?: string;
  notesGbp?: string;
};

interface CompanyEnterpriseSectionProps {
  companyId: string;
  /** Company country (form or saved) — drives billing currency for the estimate. */
  companyCountry?: string | null;
  /** Platform Billing defaults — used when custom MI/Notes fields are blank. */
  platformSettings?: SystemSettingsResponse;
  /** Live custom MI/Notes majors from the company form (unsaved). */
  rateOverrides?: EnterpriseRateOverrides;
  /** The sheet footer's slot: "Update plan" renders there, beside Cancel and
   *  Save changes. Without one it stays inline under the estimate. */
  footerSlot?: HTMLElement | null;
  /** The footer's left end: the "Enterprise inquiries" link goes there. */
  footerLeftSlot?: HTMLElement | null;
}

type PlanLifecycle = "active" | "suspended" | "ended";

/** Two sub tabs (DES 01-10-2026: "make enterprise page in two sub tabs"). */
type EnterpriseSubTab = "plan" | "people";
const ENTERPRISE_SUB_TABS: FormSheetTab<EnterpriseSubTab>[] = [
  { id: "plan", label: "Plan", icon: ClipboardList },
  { id: "people", label: "People", icon: UsersRound },
];

export function CompanyEnterpriseSection({
  companyId,
  companyCountry,
  platformSettings,
  rateOverrides,
  footerSlot,
  footerLeftSlot,
}: CompanyEnterpriseSectionProps) {
  const query = useCompanyEnterprise(companyId);
  const upsert = useUpsertCompanyEnterprise(companyId);
  const setStatus = useSetCompanyEnterpriseStatus(companyId);
  const assignSeat = useAssignCompanyEnterpriseSeat(companyId);
  const revokeSeat = useRevokeCompanyEnterpriseSeat(companyId);

  const [seatLimit, setSeatLimit] = useState(DEFAULT_SEAT_LIMIT);
  const [includedMinutes, setIncludedMinutes] = useState(DEFAULT_INCLUDED_MINUTES);
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [notes, setNotes] = useState("");
  const [skipAdvanceCharge, setSkipAdvanceCharge] = useState(false);
  const [assignUserId, setAssignUserId] = useState("");
  const [subTab, setSubTab] = useState<EnterpriseSubTab>("plan");
  const syncedKeyRef = useRef<string>("");

  const membersQuery = useUsersList(
    { company_id: companyId, limit: 100, page: 1, status: "active" },
    { enabled: Boolean(companyId) },
  );

  // Sync from server only when the saved plan fingerprint changes — do not
  // wipe in-progress edits on background refetch (26-07-2026).
  useEffect(() => {
    const plan = query.data?.plan;
    const key = plan
      ? `${plan.id}|${String(plan.updatedAt ?? "")}|${plan.includedMinutes}|${plan.status}|${plan.seatLimit}`
      : `none|${companyId}`;
    if (syncedKeyRef.current === key) return;
    syncedKeyRef.current = key;

    if (!plan) {
      const range = defaultPeriodRange();
      setSeatLimit(DEFAULT_SEAT_LIMIT);
      setIncludedMinutes(DEFAULT_INCLUDED_MINUTES);
      setPeriodStart(range.start);
      setPeriodEnd(range.end);
      setNotes("");
      return;
    }
    setSeatLimit(String(plan.seatLimit ?? 0));
    setIncludedMinutes(String(plan.includedMinutes ?? 0));
    setPeriodStart(
      plan.periodStart ? new Date(plan.periodStart).toISOString() : defaultPeriodRange().start,
    );
    setPeriodEnd(
      plan.periodEnd ? new Date(plan.periodEnd).toISOString() : defaultPeriodRange().end,
    );
    setNotes(plan.notes || "");
  }, [companyId, query.data?.plan]);

  const liveQuote = useMemo(() => {
    const q = query.data?.advanceQuote;
    const mins = Math.max(0, parseInt(includedMinutes, 10) || 0);
    const cur = (q?.currency || currencyForCountry(companyCountry) || "GBP").toUpperCase();
    const curLower = cur.toLowerCase() as "usd" | "eur" | "gbp";

    const miOverride = majorToMinorOrNull(
      cur === "USD"
        ? rateOverrides?.miUsd
        : cur === "EUR"
          ? rateOverrides?.miEur
          : rateOverrides?.miGbp,
    );
    const notesOverride = majorToMinorOrNull(
      cur === "USD"
        ? rateOverrides?.notesUsd
        : cur === "EUR"
          ? rateOverrides?.notesEur
          : rateOverrides?.notesGbp,
    );

    const listMi = settingMinor(platformSettings, `RATE_BOARDROOM_PER_MINUTE_${cur}_MINOR`);
    const listNotes = settingMinor(platformSettings, `RATE_NOTE_PER_MINUTE_${cur}_MINOR`);

    const miRateMinor =
      miOverride ?? (q?.miRateMinor != null && q.miRateMinor > 0 ? q.miRateMinor : null) ?? listMi;
    const notesRateMinor =
      notesOverride ??
      (q?.notesRateMinor != null && q.notesRateMinor > 0 ? q.notesRateMinor : null) ??
      listNotes;
    const poolRateMinor = Math.max(miRateMinor, notesRateMinor);
    const amountMinor = mins * poolRateMinor;

    return {
      currency: cur,
      currencyKey: curLower,
      includedMinutes: mins,
      miRateMinor,
      notesRateMinor,
      poolRateMinor,
      amountMinor,
      miSource: miOverride != null ? "form" : q?.miSource === "custom" ? "custom" : "list",
      notesSource: notesOverride != null ? "form" : q?.notesSource === "custom" ? "custom" : "list",
      formula: `${mins.toLocaleString()} min × ${formatMinor(poolRateMinor, cur)}/min (max MI/Notes)`,
    };
  }, [
    query.data?.advanceQuote,
    includedMinutes,
    rateOverrides?.miUsd,
    rateOverrides?.miEur,
    rateOverrides?.miGbp,
    rateOverrides?.notesUsd,
    rateOverrides?.notesEur,
    rateOverrides?.notesGbp,
    companyCountry,
    platformSettings,
  ]);

  const meter = query.data;
  const plan = meter?.plan ?? null;
  const planStatus = (plan?.status as PlanLifecycle | undefined) || null;
  const isLive = Boolean(meter?.enabled);
  const willCharge = Boolean(!skipAdvanceCharge && liveQuote && liveQuote.amountMinor > 0);

  const seatedIds = useMemo(
    () => new Set((meter?.seats ?? []).map((s) => s.userId)),
    [meter?.seats],
  );

  const assignCandidates = useMemo(() => {
    const users = membersQuery.data?.users ?? [];
    return users.filter((u) => !seatedIds.has(u.id));
  }, [membersQuery.data?.users, seatedIds]);

  const statusLabel = !plan
    ? "Not set up"
    : isLive
      ? "Live (in period)"
      : planStatus === "suspended"
        ? "Suspended"
        : planStatus === "ended"
          ? "Ended"
          : planStatus === "active"
            ? "Active (outside period)"
            : String(planStatus);

  const persistPlan = async (status: PlanLifecycle, isActivate: boolean) => {
    if (!periodStart || !periodEnd) {
      toast({
        title: "Period required",
        description: "Set both period start and end.",
      });
      return;
    }
    const seats = parseInt(seatLimit, 10) || 0;
    const mins = parseInt(includedMinutes, 10) || 0;
    if (isActivate && (seats < 1 || mins < 1)) {
      toast({
        title: "Seats and minutes required",
        description: "Set seat licenses and shared minutes to at least 1 before activating.",
      });
      return;
    }
    if (status === "active" && willCharge) {
      const ok = window.confirm(
        `${isActivate ? "Activate" : "Update"} Enterprise${
          isActivate ? " and charge advance" : " (may charge advance delta)"
        } ${formatMinor(liveQuote!.amountMinor, liveQuote!.currency)}?\n\n${
          liveQuote!.formula
        }\n\nInterpretation / Realtime stay PAYG.`,
      );
      if (!ok) return;
    }
    try {
      const result = await upsert.mutateAsync({
        status,
        seatLimit: seats,
        includedMinutes: mins,
        periodStart,
        periodEnd,
        notes: notes.trim() || null,
        skipAdvanceCharge: skipAdvanceCharge || undefined,
      });
      const adv = result.advance;
      toast({
        title: isActivate ? "Enterprise activated" : "Enterprise plan updated",
        description: adv?.charged
          ? `Advance charged: ${formatMinor(
              adv.chargeAmountMinor ?? adv.amountMinor,
              adv.currency,
            )}.`
          : adv?.message ||
            (isActivate
              ? "Shared pool live. Primary company admin auto-seated when room remains."
              : "Seat limit and shared minute pool saved."),
      });
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Save failed";
      toast({ title: "Could not save Enterprise", description: message });
    }
  };

  const onPrimarySave = () => {
    if (!plan) {
      void persistPlan("active", true);
      return;
    }
    if (isLive) {
      void persistPlan("active", false);
      return;
    }
    // Suspended / ended: save knobs without forcing activate (use Re-activate).
    if (planStatus === "suspended" || planStatus === "ended") {
      void persistPlan(planStatus, false);
      return;
    }
    // Active but outside period — treat as activate/re-open.
    void persistPlan("active", true);
  };

  const primaryBusy = upsert.isPending;
  const primaryLabel = !plan
    ? willCharge
      ? "Activate Enterprise"
      : "Activate Enterprise (no charge)"
    : isLive || planStatus === "suspended" || planStatus === "ended"
      ? "Update plan"
      : willCharge
        ? "Activate Enterprise"
        : "Activate Enterprise (no charge)";

  const onQuickStatus = async (next: PlanLifecycle) => {
    if (next === "active" && willCharge) {
      const ok = window.confirm(
        `Re-activate and charge advance ${formatMinor(
          liveQuote!.amountMinor,
          liveQuote!.currency,
        )} on the company card?`,
      );
      if (!ok) return;
    }
    try {
      const result = await setStatus.mutateAsync(next);
      const adv = result.advance;
      toast({
        title: `Enterprise marked ${next}`,
        description: adv?.charged
          ? `Advance charged: ${formatMinor(
              adv.chargeAmountMinor ?? adv.amountMinor,
              adv.currency,
            )}.`
          : undefined,
      });
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Update failed";
      toast({ title: "Status update failed", description: message });
    }
  };

  const onAssign = async () => {
    if (!assignUserId) {
      toast({ title: "Pick a member", description: "Select who should get a seat." });
      return;
    }
    try {
      await assignSeat.mutateAsync(assignUserId);
      setAssignUserId("");
      toast({ title: "Enterprise seat assigned" });
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Assign failed";
      toast({ title: "Could not assign seat", description: message });
    }
  };

  const onRevoke = async (userId: string, label: string) => {
    const ok = window.confirm(`Revoke Enterprise seat from ${label}?`);
    if (!ok) return;
    try {
      await revokeSeat.mutateAsync(userId);
      toast({ title: "Enterprise seat revoked" });
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Revoke failed";
      toast({ title: "Could not revoke seat", description: message });
    }
  };

  const planButton = (
    <Button
      type="button"
      onClick={() => void onPrimarySave()}
      disabled={primaryBusy}
      className={footerSlot ? formSheetActionClass : undefined}
    >
      {primaryBusy ? (
        <>
          <LoaderCircle className="h-4 w-4 animate-spin" />
          Saving…
        </>
      ) : (
        primaryLabel
      )}
    </Button>
  );

  if (query.isLoading) {
    return (
      <SkeletonFields fields={4} />
    );
  }

  const lastAdvance = plan?.advance;

  return (
    <div className="space-y-5">
      {/* The link sits at the left of the button row (DES 01-10-2026). */}
      {footerLeftSlot
        ? createPortal(
            <Link
              to="/admin/enterprise-inquiries"
              className="text-sm font-medium text-brand hover:underline"
            >
              Enterprise inquiries
            </Link>,
            footerLeftSlot,
          )
        : null}
      {/* Only on the Plan sub tab: People has its own Assign / Revoke. */}
      {footerSlot && subTab === "plan"
        ? createPortal(<FormSheetFooterCell>{planButton}</FormSheetFooterCell>, footerSlot)
        : null}
      {/* Status pill first, then the sub tabs (DES 01-10-2026). */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Status as a pill + menu, like the Paid pill on Session details
            (DES 01-10-2026: "is ko popover menu bana do"). */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              title="Enterprise plan status, seats and minutes"
              className={cn(
                // No focus ring: it read as a stuck orange border (DES 01-10-2026).
                "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-semibold transition-colors",
                "outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0",
                isLive
                  ? "bg-[#E8F5E9] text-[#2E7D32] dark:bg-[rgba(46,125,50,0.22)] dark:text-[#86EFAC]"
                  : planStatus === "suspended"
                    ? "bg-warning/15 text-warning"
                    : "bg-foreground/[0.06] text-muted-foreground hover:text-foreground",
              )}
            >
              {setStatus.isPending ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : null}
              {/* Says what the status is of (DES: "how would super admin know"). */}
              <Briefcase className="h-3.5 w-3.5" />
              <span className="font-medium opacity-75">Enterprise plan:</span>
              {statusLabel}
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-80 p-4">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[13px] font-semibold text-muted-foreground">
                Enterprise status
              </span>
              <span className="text-xs font-semibold text-muted-foreground">{statusLabel}</span>
            </div>
            {plan ? (
              <>
                <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3">
                  <SummaryItem
                    label="Seats"
                    value={`${meter?.seatsUsed ?? 0} / ${plan.seatLimit}`}
                  />
                  <SummaryItem
                    align="end"
                    label="Pool minutes"
                    value={`${(meter?.minutesUsed ?? 0).toLocaleString("en-GB")} / ${plan.includedMinutes.toLocaleString("en-GB")}`}
                  />
                  <p className="-mt-2 text-xs text-muted-foreground">
                    {meter?.seatsRemaining != null ? `${meter.seatsRemaining} left` : ""}
                  </p>
                  <p className="-mt-2 text-right text-xs text-muted-foreground">
                    {meter?.minutesRemaining != null
                      ? `${meter.minutesRemaining.toLocaleString("en-GB")} left`
                      : ""}
                  </p>
                </div>
                <div className="mt-3">
                  <SummaryItem
                    label="Period"
                    value={`${shortDate(plan.periodStart)} → ${shortDate(plan.periodEnd)}`}
                  />
                </div>
                {lastAdvance ? (
                  <p className="mt-3 text-xs text-muted-foreground">
                    Last advance:{" "}
                    <strong className="text-foreground">
                      {lastAdvance.status}
                      {lastAdvance.amountMinor != null && lastAdvance.currency
                        ? ` · ${formatMinor(lastAdvance.amountMinor, lastAdvance.currency)}`
                        : ""}
                    </strong>
                    {lastAdvance.chargedAt
                      ? ` · ${new Date(lastAdvance.chargedAt).toLocaleString()}`
                      : ""}
                  </p>
                ) : null}
                <DropdownMenuSeparator className="my-3" />
                {!isLive ? (
                  <DropdownMenuItem
                    disabled={setStatus.isPending}
                    onSelect={() => void onQuickStatus("active")}
                  >
                    <Play className="h-4 w-4" /> Re-activate
                  </DropdownMenuItem>
                ) : null}
                {planStatus !== "suspended" ? (
                  <DropdownMenuItem
                    disabled={setStatus.isPending}
                    onSelect={() => void onQuickStatus("suspended")}
                  >
                    <Pause className="h-4 w-4" /> Suspend
                  </DropdownMenuItem>
                ) : null}
                {planStatus !== "ended" ? (
                  <DropdownMenuItem
                    disabled={setStatus.isPending}
                    onSelect={() => void onQuickStatus("ended")}
                  >
                    <CircleStop className="h-4 w-4" /> End
                  </DropdownMenuItem>
                ) : null}
              </>
            ) : (
              <p className="mt-2 text-xs text-muted-foreground">
                No Enterprise plan yet. Set seats, minutes and period on the Plan tab, then
                activate.
              </p>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <FormSheetTabs
          compact
          tabs={ENTERPRISE_SUB_TABS}
          value={subTab}
          onChange={setSubTab}
          label="Enterprise"
        />
      </div>

      {/* Plan / People panels; the sheet fits the open one. */}
      <div className="grid">
        <div {...formSheetTabPanel(subTab, "plan")}>
          <div className="space-y-5">
            {/* Block B — Plan + money */}
            <div className="space-y-3">
              {/* Three columns, like the status card (DES 01-10-2026). */}
              <div className="grid gap-3 sm:grid-cols-3">
                <FormFieldGroup
                  label="Seat licenses"
                  // Bare fields show the label as placeholder, so the default
                  // moves here (DES 01-10-2026).
                  hint={`Default ${DEFAULT_SEAT_LIMIT} seats`}
                >
                  <Input
                    type="number"
                    min={1}
                    placeholder={DEFAULT_SEAT_LIMIT}
                    value={seatLimit}
                    onChange={(e) => setSeatLimit(e.target.value)}
                  />
                </FormFieldGroup>
                <FormFieldGroup
                  label="Shared minutes (MI + Notes)"
                  hint={`Default ${Number(DEFAULT_INCLUDED_MINUTES).toLocaleString("en-GB")} minutes`}
                >
                  <Input
                    type="number"
                    min={1}
                    placeholder={DEFAULT_INCLUDED_MINUTES}
                    value={includedMinutes}
                    onChange={(e) => setIncludedMinutes(e.target.value)}
                  />
                </FormFieldGroup>
                <div>
                  <FormFieldGroup
                    label="Internal notes"
                    // A hint like its neighbours, so the hairlines line up.
                    hint="PO or contract reference"
                  >
                    <Input
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="PO / contract ref"
                    />
                  </FormFieldGroup>
                </div>
                <FormFieldGroup label="Period start">
                  <DateTimePicker
                    value={periodStart || null}
                    onChange={(next) => setPeriodStart(next ?? "")}
                    title="Period start"
                    placeholder="Pick start date & time"
                  />
                </FormFieldGroup>
                <FormFieldGroup label="Period end">
                  <DateTimePicker
                    value={periodEnd || null}
                    onChange={(next) => setPeriodEnd(next ?? "")}
                    title="Period end"
                    placeholder="Pick end date & time"
                  />
                </FormFieldGroup>
                <div className="flex flex-wrap items-end gap-2 pb-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const range = applyPeriodPreset(1);
                      setPeriodStart(range.start);
                      setPeriodEnd(range.end);
                    }}
                  >
                    +1 month
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const range = applyPeriodPreset(12);
                      setPeriodStart(range.start);
                      setPeriodEnd(range.end);
                    }}
                  >
                    +1 year
                  </Button>
                </div>
              </div>

              {/* Advance charge, laid out like Session details' Total cost
                  (DES 01-10-2026: "show this in a good way"). */}
              {liveQuote ? (
                <div className="max-w-3xl rounded-2xl bg-foreground/[0.03] p-5">
                  <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-4">
                    <div>
                      <p className="text-[13px] font-semibold text-muted-foreground">
                        Advance charge
                      </p>
                      <p className="mt-1 text-4xl font-bold tabular-nums tracking-[-0.8px]">
                        {skipAdvanceCharge ? (
                          <span className="text-muted-foreground">No charge</span>
                        ) : (
                          <MoneyAmount
                            text={formatMinor(liveQuote.amountMinor, liveQuote.currency)}
                          />
                        )}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {skipAdvanceCharge
                          ? "Offline PO: the card is not charged. Overage stays pay as you go."
                          : "Charged to the company card when you activate."}
                      </p>
                    </div>
                    {/* minutes × rate = charge */}
                    <div className="flex items-end gap-3 tabular-nums">
                      <SummaryItem
                        label="Shared minutes"
                        value={liveQuote.includedMinutes.toLocaleString("en-GB")}
                      />
                      <span className="pb-0.5 text-muted-foreground">×</span>
                      <SummaryItem
                        label="Pool rate"
                        value={`${formatMinor(liveQuote.poolRateMinor, liveQuote.currency)}/min`}
                      />
                      <span className="pb-0.5 text-muted-foreground">=</span>
                      <SummaryItem
                        align="end"
                        label="Charge"
                        value={formatMinor(liveQuote.amountMinor, liveQuote.currency)}
                      />
                    </div>
                  </div>

                  <div className="mt-4 border-t border-foreground/10 pt-3 text-xs text-muted-foreground">
                    <p>
                      Pool rate is the higher of Meeting Intel{" "}
                      <strong className="font-semibold text-foreground">
                        {formatMinor(liveQuote.miRateMinor, liveQuote.currency)}/min
                      </strong>{" "}
                      ({RATE_SOURCE[liveQuote.miSource]}) and Notes{" "}
                      <strong className="font-semibold text-foreground">
                        {formatMinor(liveQuote.notesRateMinor, liveQuote.currency)}/min
                      </strong>{" "}
                      ({RATE_SOURCE[liveQuote.notesSource]}). {liveQuote.currency} from the company
                      country. Interpretation and Realtime stay pay as you go.
                    </p>
                    <label className="mt-3 flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={skipAdvanceCharge}
                        onChange={(e) => setSkipAdvanceCharge(e.target.checked)}
                      />
                      <span>Offline PO (skip the card charge)</span>
                    </label>
                  </div>
                </div>
              ) : (
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={skipAdvanceCharge}
                    onChange={(e) => setSkipAdvanceCharge(e.target.checked)}
                  />
                  <span>Offline PO (skip the card charge)</span>
                </label>
              )}

              {footerSlot ? null : planButton}
            </div>
          </div>
        </div>

        <div {...formSheetTabPanel(subTab, "people")}>
          {/* Block C — People */}
          {plan ? (
            <div className="space-y-3">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">
                  Primary company admin is auto-seated on activate when seat room remains. Other
                  members stay PAYG for MI + Notes until seated.
                </p>
              </div>

              {(meter?.seats?.length ?? 0) > 0 ? (
                <ul className="divide-y rounded-md border text-sm">
                  {(meter?.seats ?? []).map((seat) => {
                    const label = displayName(seat);
                    return (
                      <li
                        key={seat.userId}
                        className="flex items-center justify-between gap-2 px-3 py-2"
                      >
                        <div className="min-w-0">
                          <p className="truncate font-medium">{label}</p>
                          <p className="truncate text-xs text-muted-foreground">
                            {seat.email}
                            {seat.role ? ` · ${seat.role}` : ""}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={revokeSeat.isPending}
                          onClick={() => void onRevoke(seat.userId, label)}
                        >
                          Revoke
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground">No seats assigned yet.</p>
              )}

              {(meter?.seatsRemaining ?? 0) > 0 ? (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                  <div className="min-w-0 flex-1">
                    <FormFieldGroup label="Assign seat">
                      <Select value={assignUserId || undefined} onValueChange={setAssignUserId}>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              membersQuery.isLoading
                                ? "Loading members…"
                                : assignCandidates.length === 0
                                  ? "All members seated"
                                  : "Pick a company member"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {assignCandidates.map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.email} (
                              {u.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormFieldGroup>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={
                      assignSeat.isPending || !assignUserId || assignCandidates.length === 0
                    }
                    onClick={() => void onAssign()}
                  >
                    {assignSeat.isPending ? (
                      <>
                        <LoaderCircle className="h-4 w-4 animate-spin" />
                        Assigning…
                      </>
                    ) : (
                      "Assign"
                    )}
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Seat limit reached — raise licenses above to assign more.
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Seats can be assigned once Enterprise is activated on the Plan tab.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
